import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Trash2, Eye, FileText, Pencil, Send, CheckCircle2, XCircle, HelpCircle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import { useAuth } from "@/context/AuthContext";
import { useVehicleMap, brandOf, vehicleLabel } from "@/lib/vehicles";

function WebhookIcon({ status }) {
  // sent / failed / not_sent / pending / null(unknown)
  if (status === "sent")      return <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-label="Gönderildi" />;
  if (status === "failed")    return <XCircle      className="w-4 h-4 text-red-600"     aria-label="Başarısız" />;
  if (status === "not_sent")  return <XCircle      className="w-4 h-4 text-slate-400"   aria-label="Gönderilmedi" />;
  if (status === "pending")   return <Clock        className="w-4 h-4 text-amber-500 animate-pulse" aria-label="Beklemede" />;
  return <HelpCircle className="w-4 h-4 text-slate-300" aria-label="Bilinmiyor" />;
}

const WH_LABEL = {
  sent: "Gönderildi", failed: "Başarısız", not_sent: "Gönderilmedi",
  pending: "Beklemede", unknown: "Bilinmiyor",
};

export default function WorkOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const vmap = useVehicleMap();
  const canEdit = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [filters, setFilters] = useState({ mechanic_id: "", vehicle_plate: "", date_from: "", date_to: "" });
  const [onlyUnsent, setOnlyUnsent] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const lightbox = useLightbox();

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/work-orders", { params });
    setOrders(data);
    setSelectedIds(new Set());
  };

  useEffect(() => {
    api.get("/mechanics").then((r) => setMechanics(r.data));
    load();
    // eslint-disable-next-line
  }, []);

  const del = async (id) => {
    if (!confirm("Bu iş emrini silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/work-orders/${id}`);
      toast.success("Silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const visibleOrders = useMemo(() => {
    if (!onlyUnsent) return orders;
    // "not_sent" veya null/undefined (bilinmiyor) olanlar
    return orders.filter(o => !o.webhook_status || o.webhook_status === "not_sent" || o.webhook_status === "failed");
  }, [orders, onlyUnsent]);

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === visibleOrders.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleOrders.map(o => o.id)));
  };

  const dispatchSelected = async () => {
    if (selectedIds.size === 0) { toast.error("Seçim yok"); return; }
    const chosen = orders.filter(o => selectedIds.has(o.id));
    const alreadySent = chosen.filter(o => o.webhook_status === "sent");
    let force = false;
    if (alreadySent.length > 0) {
      if (!window.confirm(`${alreadySent.length} kayıt daha önce gönderildi. Tekrar gönderilsin mi?`)) {
        // Daha önce gönderilmiş olanları çıkar
        const kept = chosen.filter(o => o.webhook_status !== "sent").map(o => o.id);
        if (kept.length === 0) { toast.info("Gönderim iptal edildi"); return; }
        setDispatching(true); setDispatchResult(null);
        try {
          const { data } = await api.post("/work-orders/webhook/dispatch", { ids: kept, force: false });
          setDispatchResult(data);
          toast.success(`Gönderim: ${data.sent} gönderildi · ${data.failed} başarısız · ${data.already_sent} atlandı`);
          load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Gönderim hatası");
        } finally { setDispatching(false); }
        return;
      }
      force = true;
    }
    setDispatching(true); setDispatchResult(null);
    try {
      const { data } = await api.post("/work-orders/webhook/dispatch",
        { ids: Array.from(selectedIds), force });
      setDispatchResult(data);
      toast.success(`Gönderim: ${data.sent} gönderildi · ${data.failed} başarısız · ${data.already_sent} atlandı · ${data.skipped_no_url} URL yok`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderim hatası");
    } finally { setDispatching(false); }
  };

  const stats = useMemo(() => {
    const total = orders.length;
    const successful = orders.filter((o) => o.repair_status === "Başarılı Onarım").length;
    const repeats = total - successful;
    const totalPoints = orders.reduce((a, o) => a + (o.primary_points || 0) + (o.secondary_points || 0), 0);
    return { total, successful, repeats, totalPoints };
  }, [orders]);

  const token = localStorage.getItem("access_token");

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Arşiv</div>
        <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">İş Emirleri</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat label="Toplam" value={stats.total} />
        <MiniStat label="Başarılı" value={stats.successful} color="text-emerald-600" />
        <MiniStat label="Tekrar Arıza" value={stats.repeats} color="text-red-600" />
        <MiniStat label="Toplam Puan" value={`${Math.round(stats.totalPoints)}p`} color="text-[#1E3A8A]" />
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Select value={filters.mechanic_id || "all"} onValueChange={(v) => setFilters({ ...filters, mechanic_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-mechanic"><SelectValue placeholder="Usta" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Ustalar</SelectItem>
                {mechanics.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Plaka ara..." value={filters.vehicle_plate} onChange={(e) => setFilters({ ...filters, vehicle_plate: e.target.value })} data-testid="filter-plate" />
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Başlangıç tarihi</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Bitiş tarihi</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
            </div>
            <Button onClick={load} data-testid="apply-filters-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af] h-11 sm:mt-5">
              <Search className="w-4 h-4 mr-2" /> Filtrele
            </Button>
          </div>

          {isAdmin && (
            <div className="flex flex-wrap items-center gap-3 mb-3 p-2 border border-slate-200 bg-slate-50 rounded">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="only-unsent-toggle">
                <input type="checkbox" checked={onlyUnsent}
                       onChange={(e) => { setOnlyUnsent(e.target.checked); setSelectedIds(new Set()); }} />
                <span>Gönderilmemişleri filtrele</span>
              </label>
              <div className="text-xs text-slate-500" data-testid="selected-count">
                Seçili: <b>{selectedIds.size}</b> / {visibleOrders.length}
              </div>
              <Button
                size="sm"
                onClick={dispatchSelected}
                disabled={dispatching || selectedIds.size === 0}
                className="bg-[#1E3A8A] hover:bg-[#1e40af] text-white disabled:opacity-50"
                data-testid="dispatch-webhook-btn"
              >
                {dispatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Seçilenleri Yazturizm'e Gönder
              </Button>
              {dispatchResult && (
                <div className="text-xs text-slate-700" data-testid="dispatch-result">
                  <b className="text-emerald-700">{dispatchResult.sent}</b> gönderildi ·{" "}
                  <b className="text-red-700">{dispatchResult.failed}</b> başarısız ·{" "}
                  <b>{dispatchResult.already_sent}</b> atlandı
                  {dispatchResult.skipped_no_url > 0 && <> · <b>{dispatchResult.skipped_no_url}</b> URL yok</>}
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {isAdmin && (
                    <TableHead className="w-8">
                      <input type="checkbox"
                        checked={visibleOrders.length > 0 && selectedIds.size === visibleOrders.length}
                        onChange={toggleSelectAll}
                        data-testid="select-all-checkbox" />
                    </TableHead>
                  )}
                  <TableHead className="text-xs uppercase tracking-wider">Tarih</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Kod / Plaka</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Seviye</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Ana Usta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Yardımcı</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Süre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Durum</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center" title="Yazturizm gönderim durumu">WH</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Puan</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleOrders.length === 0 && (
                  <TableRow><TableCell colSpan={isAdmin ? 11 : 10} className="text-center py-8 text-slate-400">Kayıt bulunamadı</TableCell></TableRow>
                )}
                {visibleOrders.map((o) => (
                  <TableRow key={o.id} className="hover:bg-slate-50" data-testid={`row-${o.id}`}>
                    {isAdmin && (
                      <TableCell>
                        <input type="checkbox"
                          checked={selectedIds.has(o.id)}
                          onChange={() => toggleSelect(o.id)}
                          data-testid={`select-${o.id}`} />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">{new Date(o.date).toLocaleDateString("tr-TR")}</TableCell>
                    <TableCell className="font-semibold">
                      {o.vehicle_code ? (
                        <Link to={`/arac/${o.vehicle_code}`} className="hover:text-[#1E3A8A] hover:underline">
                          <span className="font-mono text-[#1E3A8A] mr-1.5">{o.vehicle_code}</span>
                          {vehicleLabel("", o.vehicle_plate, brandOf(vmap, o.vehicle_plate))}
                        </Link>
                      ) : vehicleLabel("", o.vehicle_plate, brandOf(vmap, o.vehicle_plate))}
                      {Array.isArray(o.items) && o.items.length > 0 && (
                        <div className="mt-1 space-y-0.5 font-normal">
                          {o.items.map((it, ii) => {
                            const locs = (Array.isArray(it.locations) && it.locations.length ? it.locations : [it.location || "Genel"]).join(", ");
                            const parts = [it.system_name, it.part_name, locs].filter(Boolean);
                            return (
                              <div key={ii} className="text-[11px] text-slate-500 leading-snug" data-testid={`row-${o.id}-item-${ii}`}>
                                <span className="text-slate-400">·</span> {parts.join(" · ")}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{(o.job_levels || []).join(", ") || o.job_level}</Badge></TableCell>
                    <TableCell>{o.primary_mechanic_name}</TableCell>
                    <TableCell className="text-slate-500">{o.secondary_mechanic_name || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{o.duration_minutes}dk</TableCell>
                    <TableCell>
                      {o.repair_status === "Başarılı Onarım" ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Başarılı</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Tekrar Arıza</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center" title={`Yazturizm: ${WH_LABEL[o.webhook_status] || WH_LABEL.unknown}${o.webhook_http_code ? ` (HTTP ${o.webhook_http_code})` : ""}`} data-testid={`wh-status-${o.id}`}>
                      <div className="inline-flex items-center justify-center" data-testid={`wh-icon-${o.webhook_status || "unknown"}`}>
                        <WebhookIcon status={o.webhook_status} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#1E3A8A]" title="Bu iş emrinin katkısı: Puan. Aylık hakediş 50.000 ₺ havuzdan yüzdesel dağıtılır.">
                      {Math.round((o.primary_points || 0) + (o.secondary_points || 0))}p
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(o)} data-testid={`view-${o.id}`} title="Görüntüle"><Eye className="w-4 h-4" /></Button>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/is-emirleri/${o.id}/duzenle`)}
                          data-testid={`edit-${o.id}`}
                          title="Düzenle"
                        >
                          <Pencil className="w-4 h-4 text-[#1E3A8A]" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => del(o.id)} data-testid={`delete-${o.id}`} title="Sil"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4 pr-8">
              <DialogTitle className="font-heading">İş Emri Detayı</DialogTitle>
              {canEdit && detail && (
                <Button
                  size="sm"
                  onClick={() => { const id = detail.id; setDetail(null); navigate(`/is-emirleri/${id}/duzenle`); }}
                  className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                  data-testid="detail-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" /> Düzenle
                </Button>
              )}
            </div>
          </DialogHeader>
          {detail && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Detail label="Tarih" value={new Date(detail.date).toLocaleString("tr-TR")} />
              <Detail label="Kod / Plaka / Marka" value={vehicleLabel(detail.vehicle_code, detail.vehicle_plate, brandOf(vmap, detail.vehicle_plate))} />
              <Detail label="KM" value={detail.vehicle_km} />
              <Detail label="İş Seviyesi" value={(detail.job_levels || []).join(" + ") || detail.job_level} />
              <Detail label="Ana Usta" value={detail.primary_mechanic_name} />
              <Detail label="Yardımcı" value={detail.secondary_mechanic_name || "-"} />
              <Detail label="Süre" value={`${detail.duration_minutes} dk`} />
              <Detail label="Gecikme" value={detail.delay_reason} />
              <Detail label="Mesai" value={detail.overtime ? "Evet" : "Hayır"} />
              <Detail label="Dış Servis" value={detail.outsourced ? "Evet" : "Hayır"} />
              <Detail label="Durum" value={detail.repair_status} />
              <Detail label="Girişi Yapan" value={detail.entered_by || "-"} />

              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Bildirilen Arıza</div>
                <div className="mt-1 text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">{detail.fault_reported}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yapılan İşlem</div>
                <div className="mt-1 text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">{detail.action_taken}</div>
              </div>

              {Array.isArray(detail.items) && detail.items.length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Arıza Kalemleri ({detail.items.length})</div>
                  <div className="border border-slate-200 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-1.5 w-8">#</th>
                          <th className="text-left px-2 py-1.5">Sistem · Parça · Konumlar</th>
                          <th className="text-left px-2 py-1.5">Kategori</th>
                          <th className="text-left px-2 py-1.5">Usta</th>
                          <th className="text-left px-2 py-1.5">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((it, i) => {
                          const locs = (Array.isArray(it.locations) && it.locations.length ? it.locations : [it.location || "Genel"]).join(", ");
                          return (
                            <tr key={i} className="border-t border-slate-100" data-testid={`detail-item-${i}`}>
                              <td className="px-2 py-1 font-mono text-slate-500">{i + 1}</td>
                              <td className="px-2 py-1">
                                <div className="font-semibold text-slate-800">{it.system_name || "—"}</div>
                                <div className="text-slate-500">{it.part_name || "—"} · {locs}</div>
                              </td>
                              <td className="px-2 py-1">{it.job_level || "—"}</td>
                              <td className="px-2 py-1">{it.primary_mechanic_name || "—"}</td>
                              <td className="px-2 py-1">
                                {it.repair_status === "Tekrar Arıza" ? (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Tekrar</Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Başarılı</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {detail.parts_list && detail.parts_list.length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Kullanılan Parçalar</div>
                  <div className="border border-slate-200 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr><th className="text-left px-2 py-1.5">#</th><th className="text-left px-2 py-1.5">Parça</th><th className="text-left px-2 py-1.5">Miktar</th><th className="text-left px-2 py-1.5">Açıklama</th></tr>
                      </thead>
                      <tbody>
                        {detail.parts_list.map((p, i) => (
                          <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1 font-mono text-slate-500">{p.sno || i + 1}</td><td className="px-2 py-1">{p.name}</td><td className="px-2 py-1 font-mono">{p.qty}</td><td className="px-2 py-1 text-slate-600">{p.note}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Puan Dökümü</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div>Baz Puan: <b>{detail.base_points}</b></div>
                  <div>Gecikme Çarpanı: <b>{detail.delay_multiplier}</b></div>
                  <div>Ceza: <b className="text-red-600">-{detail.penalty}</b></div>
                  <div>Net Puan: <b>{detail.net_points}</b></div>
                  <div>Ana Usta payı: <b>{detail.primary_points} p</b></div>
                  <div>Yardımcı payı: <b>{detail.secondary_points} p</b></div>
                </div>
                <div className="mt-3 p-2.5 rounded bg-blue-50 border border-blue-200 text-[11px] text-blue-900 leading-relaxed">
                  <b>ℹ Nasıl hesaplanır?</b> Bu iş emrinin katkısı yalnızca <b>puan</b> olarak gösterilir. Ustanın <b>aylık toplam puanı</b> atölyenin toplam puanına oranlanır ve <b>50.000 ₺ havuzdan yüzdesel</b> olarak hakediş dağıtılır. Nihai TL tutarları <b>Prim & Karne</b> sayfasında görülür.
                </div>
              </div>

              {detail.photo_paths && detail.photo_paths.length > 0 && (
                <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                    Yüklenen Görseller ({detail.photo_paths.length}) — tıklayınca büyür
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {detail.photo_paths.map((p, i) => {
                      const url = `${API}/files/${p}`;
                      const isPdf = p.toLowerCase().endsWith(".pdf");
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => isPdf ? window.open(url, "_blank") : lightbox.open(url)}
                          className="group relative block aspect-square border border-slate-200 rounded overflow-hidden bg-slate-50 hover:border-[#1E3A8A] transition-colors"
                          data-testid={`photo-thumb-${i}`}
                        >
                          {isPdf ? (
                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-[10px] font-mono">
                              <FileText className="w-8 h-8 mb-1" /> PDF
                            </div>
                          ) : (
                            <img src={url} alt={`ek-${i}`} className="w-full h-full object-cover" />
                          )}
                          <div className="absolute inset-0 bg-[#1E3A8A]/0 group-hover:bg-[#1E3A8A]/25 transition-colors flex items-center justify-center">
                            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Lightbox src={lightbox.src} onClose={lightbox.close} />
    </div>
  );
}

function MiniStat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`metric-num text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className="mt-0.5 text-sm text-slate-800">{value}</div>
    </div>
  );
}
